import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import type { SeriesConfig } from '@/lib/analytics/seriesConfig';

export interface SavedViewConfig {
  series: SeriesConfig[];
  chartType: 'line' | 'bar' | 'area';
}

export interface SavedAnalyticsView {
  id: string;
  workspace_id: string;
  owner_user_id: string;
  name: string;
  config: SavedViewConfig;
  is_workspace_default: boolean;
  shared: boolean;
  created_at: string;
  updated_at: string;
}

export function savedViewsQueryKey(workspaceId: string) {
  return ['saved_analytics_views', workspaceId] as const;
}

export function useSavedAnalyticsViews(workspaceId: string | null) {
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: workspaceId ? savedViewsQueryKey(workspaceId) : ['saved_analytics_views', 'noop'],
    enabled: !!workspaceId,
    staleTime: 60_000,
    queryFn: async (): Promise<SavedAnalyticsView[]> => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from('saved_analytics_views')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as SavedAnalyticsView[];
    },
  });

  const invalidate = () => {
    if (workspaceId) {
      queryClient.invalidateQueries({ queryKey: savedViewsQueryKey(workspaceId) });
    }
  };

  const createView = useMutation({
    mutationFn: async (input: {
      ownerId: string;
      name: string;
      config: SavedViewConfig;
      shared: boolean;
    }) => {
      if (!workspaceId) throw new Error('Workspace not loaded');
      const { data, error } = await supabase
        .from('saved_analytics_views')
        .insert({
          workspace_id: workspaceId,
          owner_user_id: input.ownerId,
          name: input.name,
          config: input.config,
          shared: input.shared,
        })
        .select()
        .single();
      if (error) throw error;
      return data as SavedAnalyticsView;
    },
    onSuccess: invalidate,
    onError: (err) => captureError(err as Error, { feature: 'saved-view-create' }),
  });

  const deleteView = useMutation({
    mutationFn: async (viewId: string) => {
      const { data, error } = await supabase
        .from('saved_analytics_views')
        .delete()
        .eq('id', viewId)
        .select();
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('You can only delete views you own.');
      }
    },
    onSuccess: invalidate,
    onError: (err) => captureError(err as Error, { feature: 'saved-view-delete' }),
  });

  const setWorkspaceDefault = useMutation({
    mutationFn: async (viewId: string) => {
      const { error } = await supabase.rpc('set_workspace_default_view', {
        p_view_id: viewId,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err) => captureError(err as Error, { feature: 'saved-view-set-default' }),
  });

  return {
    views: list.data ?? [],
    isLoading: list.isLoading,
    error: list.error as Error | null,
    createView,
    deleteView,
    setWorkspaceDefault,
  };
}
